/* =====================================================================
   WHOLESALE PRICING GATE — controller
   Handles: modal lifecycle, HubSpot form embed, context capture,
            GA4 events, product-context enrichment.
   =====================================================================
   Reads config from #wholesale-quote-modal-root data-* attributes.
   Delegates clicks on [data-wholesale-quote-open] anywhere in the page.
   ===================================================================== */

(function () {
  'use strict';

  const ROOT_ID = 'wholesale-quote-modal-root';
  const TEMPLATE_ID = 'wholesale-quote-modal-template';
  const SUCCESS_TEMPLATE_ID = 'wholesale-quote-modal-success-template';
  const STATE = {
    modal: null,       // active modal DOM element
    triggerButton: null, // element that opened the modal (for focus return)
    formInstance: null, // HubSpot form ref
    formEmbedded: false,
    productContext: null, // last product context captured at open
  };

  // ---------- utils ----------

  function getRoot() {
    return document.getElementById(ROOT_ID);
  }

  function readConfig() {
    const root = getRoot();
    if (!root) return null;
    return {
      formId:   root.dataset.hubspotFormId,
      portalId: root.dataset.hubspotPortalId,
      region:   root.dataset.hubspotRegion || 'na1',
      phone:    root.dataset.phone,
      phoneTel: root.dataset.phoneTel,
      sla:      root.dataset.sla,
    };
  }

  function readCookie(name) {
    const m = document.cookie.match(new RegExp('(^|;\\s*)' + name + '=([^;]+)'));
    return m ? decodeURIComponent(m[2]) : '';
  }

  function readUTM() {
    const params = new URLSearchParams(window.location.search);
    const out = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(k => {
      const v = params.get(k) || sessionStorage.getItem('eps_' + k) || '';
      out[k] = v;
      // stash on first hit so we retain across nav
      if (params.get(k)) sessionStorage.setItem('eps_' + k, v);
    });
    return out;
  }

  function trackEvent(name, params) {
    try {
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push(Object.assign({ event: name }, params || {}));
      }
      if (typeof window.gtag === 'function') {
        window.gtag('event', name, params || {});
      }
    } catch (e) {
      // never break on analytics
      console && console.debug && console.debug('[wholesale-gate] track error', e);
    }
  }

  // ---------- context capture ----------

  function captureProductContext(triggerEl) {
    // Walk up to a container carrying product data, or fall back to the page-level product info.
    let ctx = { handle: '', title: '', variantId: '', sku: '', displayedPrice: '' };

    if (triggerEl) {
      // Look for the nearest gated CTA that carries product data
      const container = triggerEl.closest('[data-wholesale-gate-cta]');
      if (container) {
        ctx.handle = container.dataset.productHandle || '';
        ctx.title = container.dataset.productTitle || '';
        ctx.variantId = container.dataset.productVariantId || '';
        ctx.sku = container.dataset.productSku || '';
        ctx.displayedPrice = container.dataset.productPrice || '';
      }
    }

    // Fallback: parse from Shopify's product JSON if it's on the page
    if (!ctx.variantId) {
      try {
        const productMeta = document.querySelector('script[type="application/json"][data-product-json], script[type="application/ld+json"]');
        // best-effort — noop if we can't parse
      } catch (_) { /* noop */ }
    }

    return ctx;
  }

  // ---------- HubSpot embed ----------

  function embedHubSpotForm(formContainer, config, ctx) {
    const utm = readUTM();
    const hutk = readCookie('hubspotutk');
    const pageUrl = window.location.href;

    if (!window.hbspt || !window.hbspt.forms || typeof window.hbspt.forms.create !== 'function') {
      // hbspt library not loaded yet — poll briefly
      let attempts = 0;
      const interval = setInterval(function () {
        attempts++;
        if (window.hbspt && window.hbspt.forms && typeof window.hbspt.forms.create === 'function') {
          clearInterval(interval);
          embedHubSpotForm(formContainer, config, ctx);
        } else if (attempts > 40) { // 40 × 100ms = 4s
          clearInterval(interval);
          formContainer.innerHTML = '<p style="color:#48586B;font-size:14px;line-height:1.5;">' +
            'Form failed to load. Please call <a href="tel:' + config.phoneTel + '" style="color:#0E5FB0;font-weight:700;">' + config.phone + '</a> or email us at ' +
            '<a href="mailto:sales@epackagesupply.com" style="color:#0E5FB0;font-weight:700;">sales@epackagesupply.com</a>.</p>';
        }
      }, 100);
      return;
    }

    // clear slot
    formContainer.innerHTML = '';
    formContainer.id = 'wq-hubspot-slot-' + Date.now();

    const contextValues = {
      product_handle: ctx.handle || '',
      product_title: ctx.title || '',
      product_variant_id: ctx.variantId || '',
      product_sku: ctx.sku || '',
      product_displayed_price: ctx.displayedPrice || '',
      page_url: pageUrl,
      quote_source: 'pdp_gated_pricing',
      utm_source: utm.utm_source,
      utm_medium: utm.utm_medium,
      utm_campaign: utm.utm_campaign,
      utm_term: utm.utm_term,
      utm_content: utm.utm_content,
    };

    // Helper: populate hidden fields inside HubSpot's form. HubSpot's modern embed
    // renders the form inside a same-origin <iframe>, so `$form` in onFormReady is
    // either the iframe's form element (jQuery-wrapped) OR we fall back to reaching
    // into the iframe's contentDocument ourselves. Legacy inline embeds are also
    // supported via the parent-doc query.
    function populateHiddenFields(formEl) {
      Object.keys(contextValues).forEach(function (name) {
        const input = formEl.querySelector('input[name="' + name + '"]');
        if (input && contextValues[name]) {
          input.value = contextValues[name];
          // Trigger change so HubSpot picks it up
          const evt = new Event('input', { bubbles: true });
          input.dispatchEvent(evt);
          const evt2 = new Event('change', { bubbles: true });
          input.dispatchEvent(evt2);
        }
      });
    }

    function findAndPopulate() {
      // 1. Try the iframe path (modern HubSpot embed)
      const iframe = formContainer.querySelector('iframe.hs-form-iframe');
      if (iframe) {
        try {
          const doc = iframe.contentDocument || (iframe.contentWindow && iframe.contentWindow.document);
          if (doc) {
            const iframeForm = doc.querySelector('form');
            if (iframeForm) {
              populateHiddenFields(iframeForm);
              return true;
            }
          }
        } catch (e) {
          console && console.debug && console.debug('[wholesale-gate] iframe access failed', e);
        }
      }
      // 2. Legacy inline form path
      const inlineForm = formContainer.querySelector('form.hs-form, form');
      if (inlineForm) {
        populateHiddenFields(inlineForm);
        return true;
      }
      return false;
    }

    STATE.formInstance = window.hbspt.forms.create({
      portalId: config.portalId,
      formId: config.formId,
      region: config.region,
      target: '#' + formContainer.id,
      onFormReady: function ($form) {
        try {
          // Try immediate populate (works for legacy inline embed)
          const formEl = ($form && $form[0]) ? $form[0] : null;
          if (formEl && formEl.tagName === 'FORM') {
            populateHiddenFields(formEl);
          }

          // Modern iframe embed: onFormReady may fire before iframe body is parented.
          // Poll briefly for iframe form and populate when available.
          let attempts = 0;
          const iframePoll = setInterval(function () {
            attempts++;
            if (findAndPopulate() || attempts > 30) { // 30 × 100ms = 3s
              clearInterval(iframePoll);
            }
          }, 100);
        } catch (e) {
          console && console.debug && console.debug('[wholesale-gate] onFormReady error', e);
        }
      },
      onFormSubmitted: function () {
        // Replace form with our in-house success state
        const dialog = STATE.modal && STATE.modal.querySelector('.wholesale-quote-modal__dialog');
        const body = STATE.modal && STATE.modal.querySelector('.wholesale-quote-modal__body');
        const successTmpl = document.getElementById(SUCCESS_TEMPLATE_ID);
        if (body && successTmpl) {
          body.innerHTML = '';
          body.appendChild(successTmpl.content.cloneNode(true));
        }

        trackEvent('gated_quote_modal_submit', {
          product_handle: ctx.handle,
          product_variant_id: ctx.variantId,
          product_sku: ctx.sku,
          product_title: ctx.title,
        });

        // Auto-close after 4s
        setTimeout(closeModal, 4500);
      },
    });

    STATE.formEmbedded = true;
  }

  // ---------- modal lifecycle ----------

  function openModal(triggerEl) {
    if (STATE.modal) return; // already open

    const config = readConfig();
    if (!config || !config.formId) {
      console.warn('[wholesale-gate] Missing HubSpot form config — cannot open modal.');
      return;
    }

    const tmpl = document.getElementById(TEMPLATE_ID);
    if (!tmpl) return;

    const ctx = captureProductContext(triggerEl);
    STATE.productContext = ctx;
    STATE.triggerButton = triggerEl;

    const clone = tmpl.content.cloneNode(true);
    const modalEl = clone.querySelector('.wholesale-quote-modal');
    document.body.appendChild(clone);
    STATE.modal = modalEl;

    // wire close controls
    const closeBtn = modalEl.querySelector('.wholesale-quote-modal__close');
    if (closeBtn) closeBtn.addEventListener('click', closeModal);
    modalEl.addEventListener('click', function (e) {
      if (e.target === modalEl) closeModal();
    });
    document.addEventListener('keydown', escHandler);

    // lock body scroll
    document.body.style.overflow = 'hidden';

    // embed form
    const slot = modalEl.querySelector('.wholesale-quote-modal__form-slot');
    embedHubSpotForm(slot, config, ctx);

    trackEvent('gated_quote_modal_open', {
      product_handle: ctx.handle,
      product_variant_id: ctx.variantId,
      product_sku: ctx.sku,
      product_title: ctx.title,
      cta_source: triggerEl && triggerEl.dataset ? (triggerEl.dataset.source || 'unknown') : 'unknown',
    });

    // focus mgmt: move focus into the modal
    setTimeout(function () {
      const firstInput = modalEl.querySelector('input, textarea, button');
      if (firstInput) firstInput.focus();
    }, 100);
  }

  function closeModal() {
    if (!STATE.modal) return;
    document.removeEventListener('keydown', escHandler);
    STATE.modal.remove();
    STATE.modal = null;
    STATE.formEmbedded = false;
    STATE.formInstance = null;
    document.body.style.overflow = '';
    // restore focus
    if (STATE.triggerButton && typeof STATE.triggerButton.focus === 'function') {
      try { STATE.triggerButton.focus(); } catch (_) { /* noop */ }
    }
    STATE.triggerButton = null;
    STATE.productContext = null;
  }

  function escHandler(e) {
    if (e.key === 'Escape' || e.keyCode === 27) closeModal();
  }

  // ---------- global click delegate ----------

  function onDocumentClick(e) {
    const trigger = e.target.closest('[data-wholesale-quote-open]');
    if (trigger) {
      e.preventDefault();
      openModal(trigger);
      return;
    }
    const phone = e.target.closest('[data-wholesale-phone-click]');
    if (phone) {
      const ctaCard = phone.closest('[data-wholesale-gate-cta]');
      trackEvent('gated_quote_phone_click', {
        product_handle: ctaCard ? ctaCard.dataset.productHandle : '',
        product_variant_id: ctaCard ? ctaCard.dataset.productVariantId : '',
      });
    }
  }

  // ---------- impression tracking (intersection observer) ----------

  function initImpressionTracking() {
    if (!('IntersectionObserver' in window)) return;
    const seen = new WeakSet();
    const io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting && !seen.has(entry.target)) {
          seen.add(entry.target);
          const el = entry.target;
          trackEvent('gated_price_shown', {
            product_handle: el.dataset.productHandle,
            product_variant_id: el.dataset.productVariantId,
            product_sku: el.dataset.productSku,
            product_title: el.dataset.productTitle,
          });
        }
      });
    }, { threshold: 0.5 });

    document.querySelectorAll('[data-wholesale-gate-cta]').forEach(function (el) {
      io.observe(el);
    });
  }

  // ---------- init ----------

  function init() {
    if (!getRoot()) return; // gate not enabled on this page
    document.addEventListener('click', onDocumentClick);
    initImpressionTracking();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
